import { c as _c } from "react/compiler-runtime";
import { Form, Formik } from 'formik';
import { useContext } from 'react';
import { Route, useHistory } from 'react-router';
import useSWR from 'swr';
import { DestinationTypes } from '../../../../../../../../typings/Integration.interface';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import LINKS from '../../../../../configs/links';
import { extractFromArray, fieldName, parseQuery, stringifyQuery } from '../../../../../utils';
import Button, { ClickableArea } from '../../../../common/base/Button';
import Divider from '../../../../common/base/Divider';
import Icon, { IconName } from '../../../../common/base/Icon';
import Text from '../../../../common/base/Text';
import Tooltip from '../../../../common/base/Tooltip';
import Dropdown from '../../../../common/Dropdown';
import DropdownMenu from '../../../../common/DropdownMenu';
import CheckboxInput from '../../../../common/Form/Fields/CheckboxInput';
import ScrollArea from '../../../../common/helpers/ScrollArea';
import { Div } from '../../../../common/helpers/StyledUtils';
import Modal from '../../../../common/Modal';
import Search from '../../../../common/Search';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { withTeamPermission } from '../../../../contexts/TeamPermissionContext';
import useSearchQuery from '../../../../hooks/useSearchQuery';
import { StyledViewContent, StyledViewLayout, StyledViewNav, StyledViewNavActions, StyledViewNavHeader, StyledViewNavSecondary } from '../../../../layouts/ViewLayout';
import { DashboardContext } from '../../DashboardContext';
import PostCreateModal from '../Create/PostCreateModal';
import destination_configuration_form_props, { DestinationConfigurationFormValues } from '../Forms/destination_configuration';
import resource_details_form_props from '../Forms/resource_details';
import DestinationsList from './DestinationsList';
const statuses = {
  active: 'Active',
  disabled: 'Disabled'
};
const DEFAULT_PAGE_SIZE = 50;
const sort_options = {
  created_at_desc: {
    label: 'Newest -> Oldest',
    dir: 'desc',
    key: 'created_at'
  },
  created_at_asc: {
    label: 'Oldest -> Newest',
    dir: 'asc',
    key: 'created_at'
  },
  name_asc: {
    label: 'A -> Z',
    dir: 'asc',
    key: 'name'
  },
  name_desc: {
    label: 'Z -> A',
    dir: 'desc',
    key: 'name'
  },
  updated_at: {
    label: 'Last updated',
    dir: 'desc',
    key: 'updated_at'
  }
} as const;
const sort_keys = Object.keys(sort_options) as (keyof typeof sort_options)[];
export interface DestinationsListQueryParams {
  selected_destination_id?: string;

  // Pagination & sorting
  order_by?: keyof typeof sort_options;
  next?: string[];
  prev?: string[];

  // Filters
  search_term?: string;
  status?: (keyof typeof statuses)[];
  type?: string | string[];
  connection_id?: string | string[];
}
interface DestinationsListFiltersProps {
  id?: string;
  name?: {
    contains: string;
  };
  status?: keyof typeof statuses;
  type?: string | string[];
  disabled?: boolean;
  disabled_at?: {
    gte: string;
  };
  connection_id?: string;

  // Pagination & sorting
  order_by?: (typeof sort_options)[keyof typeof sort_options]['key'];
  dir?: 'asc' | 'desc';
  next?: string;
  prev?: string;
  limit?: number;
}
const extractFiltersFromQuery = (parsed_query: DestinationsListQueryParams): DestinationsListFiltersProps => {
  let name: {
    contains: string;
  } | undefined = undefined;
  let id: string | undefined = undefined;
  if (parsed_query.search_term) {
    if (parsed_query.search_term.startsWith('dst_')) {
      id = parsed_query.search_term.trim();
    } else {
      name = {
        contains: parsed_query.search_term
      };
    }
  }
  const status = extractFromArray(parsed_query.status);
  const all_statuses = parsed_query.status?.length === Object.keys(statuses).length;
  return {
    id,
    name,
    type: parsed_query.type,
    status: all_statuses ? undefined : status,
    disabled: all_statuses ? undefined : status?.includes('active') ? false : undefined,
    disabled_at: all_statuses ? undefined : status?.includes('disabled') ? {
      gte: new Date('2020-01-01').toISOString()
    } : undefined,
    connection_id: extractFromArray(parsed_query.connection_id),
    order_by: parsed_query.order_by ? sort_options[parsed_query.order_by]?.key : undefined,
    dir: parsed_query.order_by ? sort_options[parsed_query.order_by]?.dir : undefined,
    next: extractFromArray(parsed_query.next || []) as string,
    prev: extractFromArray(parsed_query.prev || []) as string,
    limit: DEFAULT_PAGE_SIZE
  };
};
const TypesFilter: React.FC<{
  query: DestinationsListQueryParams;
  updateSearchQuery: (query: DestinationsListQueryParams) => void;
  destination_types: DestinationTypes;
}> = t0 => {
  const $ = _c(13);
  const {
    query,
    updateSearchQuery,
    destination_types
  } = t0;
  let t1;
  if ($[0] !== query.type) {
    t1 = query.type ? typeof query.type === "string" ? {
      [query.type]: true
    } : query.type.reduce(_temp, {}) : {};
    $[0] = query.type;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      type: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== updateSearchQuery) {
    t3 = values => {
      const type = Object.entries(values.type).filter(_temp2).map(_temp3);
      updateSearchQuery({
        type: type.length > 0 ? type : undefined
      });
    };
    $[4] = updateSearchQuery;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== destination_types || $[7] !== query.type) {
    t4 = t5 => {
      const {
        submitForm
      } = t5;
      return <Dropdown parent_width={{
        min: 192
      }} placement="bottom-start" icon="filter_list" label="Type" onToggle={() => {
        submitForm();
      }} expand_icon="chevron_down" badge={query.type && query.type.length > 0 ? {
        label: String(query.type.length)
      } : undefined} outline={true}>{() => <Form><Div flex={{
            direction: "column"
          }}><ScrollArea p={1} style={{
              maxHeight: "calc(90vh - 180px)"
            }}>{destination_types && Object.entries(destination_types).sort(_temp4).map(_temp5)}</ScrollArea></Div></Form>}</Dropdown>;
    };
    $[6] = destination_types;
    $[7] = query.type;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = <Formik initialValues={t2} onSubmit={t3}>{t4}</Formik>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
const CreateDestinationModal: React.FC<{
  mutateDestinations: () => void;
}> = t0 => {
  const $ = _c(16);
  const {
    mutateDestinations
  } = t0;
  const history = useHistory();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    team
  } = useContext(DashboardContext);
  const {
    addToast
  } = useToasts();
  let t1;
  if ($[0] !== team?.max_events_per_second) {
    t1 = destination_configuration_form_props.getInitialValues(undefined, undefined, team?.max_events_per_second);
    $[0] = team?.max_events_per_second;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== HookdeckAPI.destinations || $[3] !== addToast || $[4] !== history || $[5] !== mutateDestinations) {
    t2 = values => {
      const destination = {
        name: values.name,
        ...destination_configuration_form_props.postprocessValues(values)
      };
      HookdeckAPI.destinations.create(destination).then(() => {
        mutateDestinations();
        addToast("success", "New destination created");
        history.push("/destinations");
      }).catch(() => addToast("error", "Failed to create destination"));
    };
    $[2] = HookdeckAPI.destinations;
    $[3] = addToast;
    $[4] = history;
    $[5] = mutateDestinations;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== HookdeckAPI.destinations.nameIsUsed) {
    t3 = async v => ({
      ...(await resource_details_form_props.validate(v, HookdeckAPI.destinations.nameIsUsed).catch(_temp6)),
      ...(await destination_configuration_form_props.validate(v as DestinationConfigurationFormValues))
    });
    $[7] = HookdeckAPI.destinations.nameIsUsed;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== history) {
    t4 = props => <Form><Modal title="Add new destination" is_submitting={props.isSubmitting} onClose={() => {
        history.push("/destinations");
      }}><Div m={{
          b: 0
        }}><destination_configuration_form_props.Fields prefix="" /><Div m={{
            x: -4,
            t: 4,
            b: 3
          }}><Divider /></Div><Div flex={{
            align: "center",
            justify: "flex-end",
            gap: 3
          }} m={{
            b: -1
          }}><Button type="button" onClick={() => history.push("/destinations")} outline={true}>Cancel</Button><Button icon="save" disabled={props.isSubmitting || !props.isValid || !props.dirty} type="submit" onClick={() => props.handleSubmit()} primary={true}>Save</Button></Div></Div></Modal></Form>;
    $[9] = history;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t4) {
    t5 = <Formik initialValues={t1} onSubmit={t2} validate={t3}>{t4}</Formik>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
const DestinationsView: React.FC = () => {
  const $ = _c(85);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    destination_types
  } = useContext(DashboardContext);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      order_by: "created_at_desc"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    query,
    updateSearchQuery
  } = useSearchQuery(t0);
  let t1;
  if ($[1] !== query || $[2] !== updateSearchQuery) {
    t1 = term => {
      updateSearchQuery({
        ...query,
        search_term: term || undefined
      });
    };
    $[1] = query;
    $[2] = updateSearchQuery;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const setSearchTerm = t1;
  let filters;
  let page_size;
  let t2;
  let t3;
  if ($[4] !== query) {
    filters = {
      ...extractFiltersFromQuery(query),
      include: "config.auth"
    };
    page_size = filters.limit || DEFAULT_PAGE_SIZE;
    t2 = useSWR;
    t3 = APIMethodKeys.destinations.list(filters);
    $[4] = query;
    $[5] = filters;
    $[6] = page_size;
    $[7] = t2;
    $[8] = t3;
  } else {
    filters = $[5];
    page_size = $[6];
    t2 = $[7];
    t3 = $[8];
  }
  let t4;
  if ($[9] !== HookdeckAPI.destinations || $[10] !== filters) {
    t4 = () => HookdeckAPI.destinations.list(filters);
    $[9] = HookdeckAPI.destinations;
    $[10] = filters;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      keepPreviousData: true,
      dedupingInterval: 150
    };
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const {
    data,
    mutate,
    isLoading
  } = t2(t3, t4, t5);
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = APIMethodKeys.destinations.count({});
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== HookdeckAPI.destinations) {
    t7 = () => HookdeckAPI.destinations.count({});
    $[14] = HookdeckAPI.destinations;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const {
    data: count_data
  } = useSWR(t6, t7);
  let t8;
  if ($[16] !== query.created) {
    t8 = query.created && <PostCreateModal />;
    $[16] = query.created;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      y: 5
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      align: "center",
      justify: "space-between",
      gap: 2
    };
    t11 = <Text semi={true} ellipsis={true}>Destinations</Text>;
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Div flex={t10}>{t11}<Tooltip tooltip="Destinations represent any endpoint to which your events can be routed. Hookdeck supports three types: HTTP (public endpoint), CLI (local testing environments), and Mock API (Hookdeck-provided endpoint that accepts all API requests)." side="bottom" max_width={380} cta={{
        label: "Destinations docs",
        to: LINKS.product_docs.destinations
      }}><Icon icon="info" muted={true} /></Tooltip></Div>;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <StyledViewNavHeader p={t9}>{t12}<StyledViewNavActions><Button.Permission to={_temp7} primary={true} icon="add_circle">New destination</Button.Permission></StyledViewNavActions></StyledViewNavHeader>;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      b: 3
    };
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      justify: "space-between",
      align: "center",
      gap: 3
    };
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      align: "center",
      gap: 3
    };
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      px: 300
    };
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  const t18 = query.search_term || "";
  let t19;
  if ($[27] !== setSearchTerm || $[28] !== t18) {
    t19 = <Div w={t17}><Search onChange={setSearchTerm} value={t18} placeholder="Filter by ID or name..." small={true} /></Div>;
    $[27] = setSearchTerm;
    $[28] = t18;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      min: 200
    };
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  const t21 = statuses[filters.status as keyof typeof statuses] || "Status";
  const t22 = !filters.status;
  let t23;
  if ($[31] !== updateSearchQuery) {
    t23 = () => updateSearchQuery({
      status: undefined
    });
    $[31] = updateSearchQuery;
    $[32] = t23;
  } else {
    t23 = $[32];
  }
  let t24;
  if ($[33] !== t22 || $[34] !== t23) {
    t24 = {
      label: "All",
      selected: t22,
      onClick: t23
    };
    $[33] = t22;
    $[34] = t23;
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  let t25;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = Object.entries(statuses);
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  let t26;
  if ($[37] !== filters.status || $[38] !== updateSearchQuery) {
    t26 = t25.map(t27 => {
      const [key, label] = t27;
      return {
        label,
        selected: filters.status === key,
        onClick: () => updateSearchQuery({
          status: [key]
        })
      };
    });
    $[37] = filters.status;
    $[38] = updateSearchQuery;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] !== t24 || $[41] !== t26) {
    t27 = [t24, ...t26];
    $[40] = t24;
    $[41] = t26;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  let t28;
  if ($[43] !== t21 || $[44] !== t27) {
    t28 = <DropdownMenu icon="filter_list" placement="bottom-start" outline={true} expand_icon="chevron_down" parent_width={t20} label={t21} options={t27} />;
    $[43] = t21;
    $[44] = t27;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  if ($[46] !== destination_types || $[47] !== query || $[48] !== updateSearchQuery) {
    t29 = <TypesFilter query={query} updateSearchQuery={updateSearchQuery} destination_types={destination_types} />;
    $[46] = destination_types;
    $[47] = query;
    $[48] = updateSearchQuery;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] !== t19 || $[51] !== t28 || $[52] !== t29) {
    t30 = <Div flex={t16}>{t19}{t28}{t29}</Div>;
    $[50] = t19;
    $[51] = t28;
    $[52] = t29;
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  let t31;
  if ($[54] !== query.order_by || $[55] !== updateSearchQuery) {
    t31 = sort_keys.map(key_0 => ({
      onClick: () => updateSearchQuery({
        order_by: key_0
      }),
      label: sort_options[key_0]?.label,
      selected: query.order_by === key_0
    }));
    $[54] = query.order_by;
    $[55] = updateSearchQuery;
    $[56] = t31;
  } else {
    t31 = $[56];
  }
  let t32;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      min: 150
    };
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  const t33 = sort_options[query.order_by || "name"]?.label;
  let t34;
  if ($[58] !== t31 || $[59] !== t33) {
    t34 = <DropdownMenu options={t31} p={0} parent_width={t32} placement="bottom-end" label_prefix="Order by" label={t33} expand_icon="chevron_down" icon="sort" outline={true} />;
    $[58] = t31;
    $[59] = t33;
    $[60] = t34;
  } else {
    t34 = $[60];
  }
  let t35;
  if ($[61] !== t30 || $[62] !== t34) {
    t35 = <StyledViewNav>{t13}<StyledViewNavSecondary p={t14}><Div w={100} flex={t15}>{t30}{t34}</Div></StyledViewNavSecondary></StyledViewNav>;
    $[61] = t30;
    $[62] = t34;
    $[63] = t35;
  } else {
    t35 = $[63];
  }
  let t36;
  if ($[64] !== data?.models) {
    t36 = data?.models || [];
    $[64] = data?.models;
    $[65] = t36;
  } else {
    t36 = $[65];
  }
  const t37 = count_data?.count || 0;
  const t38 = data?.pagination;
  let t39;
  if ($[66] !== page_size || $[67] !== t38) {
    t39 = {
      ...t38,
      page_size
    };
    $[66] = page_size;
    $[67] = t38;
    $[68] = t39;
  } else {
    t39 = $[68];
  }
  const t40 = !!query.search_term || !!query.status || !!query.type;
  let t41;
  if ($[69] !== isLoading || $[70] !== mutate || $[71] !== t36 || $[72] !== t37 || $[73] !== t39 || $[74] !== t40) {
    t41 = <DestinationsList destinations={t36} total={t37} loading={isLoading} pagination={t39} mutate={mutate} filtering={t40} />;
    $[69] = isLoading;
    $[70] = mutate;
    $[71] = t36;
    $[72] = t37;
    $[73] = t39;
    $[74] = t40;
    $[75] = t41;
  } else {
    t41 = $[75];
  }
  let t42;
  if ($[76] !== mutate) {
    t42 = <Route exact={true} path="/destinations/new" render={() => <CreateDestinationModal mutateDestinations={mutate} />} />;
    $[76] = mutate;
    $[77] = t42;
  } else {
    t42 = $[77];
  }
  let t43;
  if ($[78] !== t35 || $[79] !== t41 || $[80] !== t42) {
    t43 = <StyledViewLayout><StyledViewContent>{t35}{t41}{t42}</StyledViewContent></StyledViewLayout>;
    $[78] = t35;
    $[79] = t41;
    $[80] = t42;
    $[81] = t43;
  } else {
    t43 = $[81];
  }
  let t44;
  if ($[82] !== t43 || $[83] !== t8) {
    t44 = <>{t8}{t43}</>;
    $[82] = t43;
    $[83] = t8;
    $[84] = t44;
  } else {
    t44 = $[84];
  }
  return t44;
};
export default withTeamPermission(DestinationsView, 'member');
function _temp(obj, key) {
  return {
    ...obj,
    [key]: true
  };
}
function _temp2(t0) {
  const [, value] = t0;
  return value;
}
function _temp3(t0) {
  const [key_0] = t0;
  return key_0;
}
function _temp4(a, b) {
  if (a[1].category === "generic" && b[1].category !== "generic") {
    return -1;
  }
  if (a[1].category !== "generic" && b[1].category === "generic") {
    return 1;
  }
  return a[1].label.localeCompare(b[1].label);
}
function _temp5(t0) {
  const [key_1, config] = t0;
  const fieldId = fieldName(key_1, "type");
  return <ClickableArea key={key_1} rounded={true}><CheckboxInput label={config.label} icon={config.icon as IconName} name={fieldId} p={{
      x: 2,
      y: 1.5
    }} /></ClickableArea>;
}
function _temp6(e) {
  return e;
}
function _temp7(location) {
  return {
    ...location,
    pathname: "/destinations/new",
    search: `?${stringifyQuery({
      ...parseQuery(location.search),
      default_destination_id: undefined
    })}`,
    state: {
      scroll: false
    }
  };
}